import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mic, Upload, Square, CheckCircle, Loader2, ArrowRight, ArrowLeft, XCircle } from 'lucide-react';
import { api } from '../contexts/AuthContext';
import { toast } from 'sonner';

const categories = ['Operations', 'Safety', 'Quality', 'Maintenance', 'Training', 'HR', 'Sales', 'Customer Service', 'Other'];

export default function NewCapturePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [captureId, setCaptureId] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [sopData, setSopData] = useState(null);
  const [category, setCategory] = useState('Operations');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [micError, setMicError] = useState(null);
  const [errorInfo, setErrorInfo] = useState(null);
  const [cancelled, setCancelled] = useState(false);
  const pollRef = useRef(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  const startRecording = async () => {
    setMicError(null);
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { setMicError('Browser does not support recording.'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => { stream.getTracks().forEach(t => t.stop()); await uploadAudio(new Blob(chunksRef.current, { type: 'audio/webm' }), 'recording.webm'); };
      mediaRecorder.start(); setIsRecording(true); setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (err) {
      if (err.name === 'NotAllowedError') setMicError('denied');
      else setMicError(`Recording failed: ${err.message}`);
    }
  };

  const stopRecording = () => { if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.stop(); setIsRecording(false); clearInterval(timerRef.current); } };

  const handleFileSelect = async (e) => { const file = e.target.files[0]; if (!file) return; if (file.size > 500 * 1024 * 1024) { toast.error('File too large. Max 500MB.'); return; } await uploadAudio(file, file.name); };

  const uploadAudio = async (fileOrBlob, filename) => {
    setStep(2); setLoading(true); setProgress('Uploading audio...');
    try {
      const formData = new FormData(); formData.append('file', fileOrBlob, filename);
      const { data } = await api.post(`/api/captures/upload`, formData);
      setCaptureId(data.capture_id);
      
      toast.success('Upload complete — processing started');
      
      if (data.file_size > 24 * 1024 * 1024) {
        setProgress('Large file detected — splitting into chunks...');
      } else {
        setProgress('Transcribing audio...');
      }
      
      // Poll for full pipeline completion
      await pollPipelineStatus(data.capture_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed. Please try again.');
      setStep(1);
    } finally { setLoading(false); }
  };

  const STATUS_MESSAGES = {
    transcribing: 'Transcribing audio...',
    chunking: 'Large file — splitting into chunks and transcribing...',
    transcribed: 'Transcription complete — generating SOP...',
    generating_sop: 'AI is structuring your transcript into a professional SOP...',
    saving_sop: 'Saving SOP to your Company Brain...',
    indexing: 'Indexing SOP for search — almost done...',
  };

  const pollPipelineStatus = async (capId) => {
    pollRef.current = true;
    const maxAttempts = 180;
    for (let i = 0; i < maxAttempts; i++) {
      if (!pollRef.current) { return; } // cancelled
      await new Promise(r => setTimeout(r, 5000));
      if (!pollRef.current) { return; }
      try {
        const { data } = await api.get(`/api/captures/${capId}/status`);
        
        if (data.status === 'complete' && data.sop_id) {
          setSopData({ sop_id: data.sop_id, title: data.sop_title, content: null });
          try {
            const sopRes = await api.get(`/api/sops/${data.sop_id}`);
            setSopData({ ...sopRes.data, sop_id: data.sop_id });
            setTranscript(data.transcript || '');
            setCategory(sopRes.data.category || 'Operations');
          } catch {}
          toast.success('SOP created and indexed in your Company Brain!');
          setStep(5);
          return;
        }
        
        if (data.status === 'error') {
          const stage = data.error_stage || 'unknown';
          const msg = data.error_message || 'Processing failed.';
          setErrorInfo({ message: msg, stage });
          setStep(7); // new error step
          return;
        }
        
        setProgress(STATUS_MESSAGES[data.status] || 'Processing...');
        
        if (['generating_sop', 'saving_sop', 'indexing'].includes(data.status)) {
          setStep(4);
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }
    
    setErrorInfo({ message: 'Processing is taking longer than expected. Your SOP will appear on the dashboard when ready.', stage: 'timeout' });
    setStep(7);
  };

  const cancelProcessing = () => {
    pollRef.current = false;
    setCancelled(true);
    setLoading(false);
    setStep(1);
    toast('Processing cancelled. The job may still complete in the background.');
  };

  const generateSOP = async () => {
    setStep(4); setLoading(true);
    try { const { data } = await api.post(`/api/captures/${captureId}/generate-sop`); setSopData(data); setCategory(data.category || 'Operations'); setStep(5); }
    catch (err) { toast.error(err.response?.data?.detail || 'Generation failed.'); setStep(3); }
    finally { setLoading(false); }
  };

  const saveSOP = async () => {
    setLoading(true);
    try { await api.put(`/api/sops/${sopData.sop_id}`, { title: sopData.title, content: sopData.content, category }); setStep(6); toast.success('SOP saved!'); }
    catch (err) { toast.error('Failed to save.'); }
    finally { setLoading(false); }
  };

  const formatTime = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;

  return (
    <div className="max-w-3xl mx-auto" data-testid="new-capture-page">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">{[1,2,3,4,5,6].map(s => <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? 'bg-[#AAFF00]' : 'bg-[#374151]'}`} />)}</div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">
          {step===1&&'New Capture'}{step===2&&'Processing Audio'}{step===3&&'Review Transcript'}{step===4&&'Generating SOP'}{step===5&&'Review SOP'}{step===6&&'Saved!'}{step===7&&'Processing Failed'}
        </h1>
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <p className="text-[#9CA3AF] mb-6">Record yourself explaining a process, or upload an existing audio file.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button onClick={startRecording} className="p-8 bg-[#111827] rounded-lg border border-[#374151] hover:border-[#AAFF00]/50 transition-all text-left" data-testid="record-btn">
              <div className="p-3 rounded-lg bg-[#1F2937] w-fit mb-4"><Mic size={24} className="text-red-400" /></div>
              <p className="font-semibold text-[#F9FAFB] text-lg">Record Now</p>
              <p className="text-sm text-[#9CA3AF] mt-1">Use your microphone to record</p>
            </button>
            <button onClick={() => fileInputRef.current?.click()} className="p-8 bg-[#111827] rounded-lg border border-[#374151] hover:border-[#3B82F6]/50 transition-all text-left" data-testid="upload-btn">
              <div className="p-3 rounded-lg bg-[#1F2937] w-fit mb-4"><Upload size={24} className="text-[#3B82F6]" /></div>
              <p className="font-semibold text-[#F9FAFB] text-lg">Upload Audio</p>
              <p className="text-sm text-[#9CA3AF] mt-1">M4A, MP3, WAV, AAC, OGG and more (up to 500MB)</p>
            </button>
          </div>
          <input ref={fileInputRef} type="file" accept=".mp3,.m4a,.wav,.mp4,.webm,audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/webm,video/mp4,video/webm" onChange={handleFileSelect} className="hidden" data-testid="file-input" />
          {micError && (
            <div className="mt-6 p-5 bg-[#111827] rounded-lg border border-[#374151]" data-testid="mic-error-panel">
              {micError === 'denied' ? (<div><p className="font-medium text-[#F9FAFB] mb-2">Microphone access blocked</p><ol className="text-sm text-[#9CA3AF] space-y-1 list-decimal list-inside mb-3"><li>Click the lock icon in your address bar</li><li>Set Microphone to Allow</li><li>Reload the page</li></ol><div className="flex gap-2"><Button variant="outline" size="sm" onClick={() => window.location.reload()} className="border-[#374151] text-[#9CA3AF] hover:bg-[#1F2937]" data-testid="reload-page-btn">Reload</Button><Button variant="outline" size="sm" onClick={() => { setMicError(null); fileInputRef.current?.click(); }} className="border-[#374151] text-[#9CA3AF] hover:bg-[#1F2937]" data-testid="upload-instead-btn">Upload instead</Button></div></div>
              ) : (<div><p className="text-sm text-[#9CA3AF]">{micError}</p></div>)}
            </div>
          )}
          {isRecording && (
            <div className="mt-6 p-6 bg-[#111827] rounded-lg border border-red-500/30 text-center">
              <div className="flex items-center justify-center gap-3 mb-4"><div className="w-3 h-3 rounded-full bg-red-500 animate-pulse-recording" /><span className="text-lg font-medium text-[#F9FAFB]">Recording</span></div>
              <p className="text-3xl font-mono text-[#F9FAFB] mb-4" data-testid="recording-timer">{formatTime(recordingTime)}</p>
              <Button onClick={stopRecording} className="bg-red-500 hover:bg-red-600 text-white gap-2" data-testid="stop-recording-btn"><Square size={16} fill="currentColor" /> Stop</Button>
            </div>
          )}
        </div>
      )}

      {step === 2 && (<div className="text-center py-16"><Loader2 size={40} className="mx-auto text-[#AAFF00] animate-spin mb-4" /><p className="text-lg text-[#F9FAFB]">{progress}</p><p className="text-sm text-[#9CA3AF] mt-2">This may take a moment.</p><Button variant="outline" onClick={cancelProcessing} className="mt-6 border-[#374151] text-[#9CA3AF] hover:bg-[#1F2937] hover:text-[#F9FAFB] gap-2" data-testid="cancel-processing-btn"><XCircle size={16} /> Cancel</Button></div>)}

      {step === 3 && (
        <div className="space-y-4">
          <p className="text-[#9CA3AF]">Review and edit the transcript before generating your SOP.</p>
          <Textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} className="min-h-[300px] text-sm bg-[#1F2937] border-[#374151] text-[#F9FAFB] placeholder:text-[#9CA3AF]" data-testid="transcript-textarea" />
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} className="gap-2 border-[#374151] text-[#9CA3AF] hover:bg-[#1F2937] hover:text-[#F9FAFB]" data-testid="back-to-capture-btn"><ArrowLeft size={16} /> Start Over</Button>
            <Button onClick={generateSOP} className="bg-[#AAFF00] text-[#080812] hover:bg-[#c4ff4d] gap-2 font-semibold" data-testid="generate-sop-btn">Generate SOP <ArrowRight size={16} /></Button>
          </div>
        </div>
      )}

      {step === 4 && (<div className="text-center py-16"><Loader2 size={40} className="mx-auto text-[#AAFF00] animate-spin mb-4" /><p className="text-lg text-[#F9FAFB]">{progress || 'Generating your SOP...'}</p><p className="text-sm text-[#9CA3AF] mt-2">AI is structuring your transcript.</p><Button variant="outline" onClick={cancelProcessing} className="mt-6 border-[#374151] text-[#9CA3AF] hover:bg-[#1F2937] hover:text-[#F9FAFB] gap-2" data-testid="cancel-generating-btn"><XCircle size={16} /> Cancel</Button></div>)}

      {step === 5 && sopData && (
        <div className="space-y-4">
          <div className="bg-[#111827] rounded-lg border border-[#374151] p-6">
            <input value={sopData.title} onChange={(e) => setSopData({...sopData, title: e.target.value})} className="text-xl font-semibold text-[#F9FAFB] w-full border-none outline-none bg-transparent mb-4" data-testid="sop-title-input" />
            <div className="mb-4">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-48 bg-[#1F2937] border-[#374151] text-[#F9FAFB]" data-testid="sop-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#374151]">{categories.map(c => <SelectItem key={c} value={c} className="text-[#F9FAFB] focus:bg-[#1F2937]">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea value={sopData.content} onChange={(e) => setSopData({...sopData, content: e.target.value})} className="min-h-[400px] text-sm font-mono bg-[#1F2937] border-[#374151] text-[#F9FAFB]" data-testid="sop-content-textarea" />
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(3)} className="gap-2 border-[#374151] text-[#9CA3AF] hover:bg-[#1F2937] hover:text-[#F9FAFB]" data-testid="back-to-transcript-btn"><ArrowLeft size={16} /> Back</Button>
            <Button onClick={saveSOP} disabled={loading} className="bg-[#AAFF00] text-[#080812] hover:bg-[#c4ff4d] gap-2 font-semibold" data-testid="save-sop-btn">{loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />} Save to Library</Button>
          </div>
        </div>
      )}

      {step === 6 && (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-[#AAFF00]/10 flex items-center justify-center mx-auto mb-4"><CheckCircle size={32} className="text-[#AAFF00]" /></div>
          <h2 className="text-2xl font-semibold text-[#F9FAFB] mb-2">SOP Saved!</h2>
          <p className="text-[#9CA3AF] mb-6">Your SOP has been added to the library.</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => navigate('/library')} className="border-[#374151] text-[#9CA3AF] hover:bg-[#1F2937] hover:text-[#F9FAFB]" data-testid="go-to-library-btn">View Library</Button>
            <Button onClick={() => { setStep(1); setCaptureId(null); setTranscript(''); setSopData(null); setErrorInfo(null); }} className="bg-[#AAFF00] text-[#080812] hover:bg-[#c4ff4d] font-semibold" data-testid="new-capture-again-btn">New Capture</Button>
          </div>
        </div>
      )}

      {step === 7 && errorInfo && (
        <div className="py-12 animate-fade-in-up" data-testid="pipeline-error">
          <div className="bg-[#111827] rounded-lg border border-red-500/30 p-8 text-center max-w-lg mx-auto">
            <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <XCircle size={28} className="text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-[#F9FAFB] mb-2">Processing Failed</h2>
            <p className="text-[#9CA3AF] text-sm mb-1">
              Stage: <span className="text-[#F9FAFB] font-medium">{errorInfo.stage === 'transcribing' ? 'Transcription' : errorInfo.stage === 'generating_sop' ? 'SOP Generation' : errorInfo.stage === 'saving_sop' ? 'Saving to Database' : errorInfo.stage === 'timeout' ? 'Timeout' : errorInfo.stage}</span>
            </p>
            <p className="text-red-400 text-sm mb-6 bg-red-500/5 rounded-md p-3 border border-red-500/10 mt-3" data-testid="error-message">{errorInfo.message}</p>
            <div className="flex justify-center gap-3">
              {captureId && errorInfo.stage !== 'timeout' && (
                <Button variant="outline" onClick={() => { setErrorInfo(null); setStep(2); setProgress('Retrying...'); setLoading(true); pollPipelineStatus(captureId).finally(() => setLoading(false)); }} className="border-[#374151] text-[#9CA3AF] hover:bg-[#1F2937] hover:text-[#F9FAFB] gap-2" data-testid="retry-btn">Try Again</Button>
              )}
              <Button onClick={() => { setStep(1); setCaptureId(null); setTranscript(''); setSopData(null); setErrorInfo(null); setLoading(false); }} className="bg-[#AAFF00] text-[#080812] hover:bg-[#c4ff4d] font-semibold gap-2" data-testid="start-over-btn">Start Over</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
